import React from "react"

export default function HubspotForm({
    portalId = "21250167",
    formId,
    region = "na1",
    version = "auto", // "auto", "v2", or "embed"
    target = null, // custom target selector for v2 forms
    onSubmit,
    onFormInteraction,
}) {
    const [isClient, setIsClient] = React.useState(false)
    const [isLoading, setIsLoading] = React.useState(true)
    const [hasError, setHasError] = React.useState(false)
    const scriptLoaded = React.useRef(false)
    const formCreated = React.useRef(false)
    const hasInteracted = React.useRef(false)

    // Ensure client-side rendering
    React.useEffect(() => {
        setIsClient(true)
    }, [])

    const getFormVersion = React.useCallback(() => {
        if (version === "v2") return "v2"
        if (version === "embed") return "embed"
        return "v2"
    }, [version])

    const createV2Form = React.useCallback(() => {
        return new Promise((resolve, reject) => {
            if (formCreated.current) {
                resolve()
                return
            }

            const targetElement = target || `#hubspot-form-${formId}`

            let attempts = 0
            const checkHbspt = () => {
                attempts++

                if (window.hbspt && window.hbspt.forms && window.hbspt.forms.create) {
                    try {
                        window.hbspt.forms.create({
                            portalId: portalId,
                            formId: formId,
                            region: region,
                            target: targetElement,
                            onFormReady: function ($form) {
                                formCreated.current = true

                                // Add interaction tracking
                                if (onFormInteraction) {
                                    const formElement = $form.length ? $form[0] : $form
                                    const inputs = formElement.querySelectorAll(
                                        "input, textarea, select"
                                    )

                                    const handleInteraction = () => {
                                        if (!hasInteracted.current) {
                                            hasInteracted.current = true
                                            if (onFormInteraction) onFormInteraction()
                                        }
                                    }

                                    inputs.forEach(input => {
                                        input.addEventListener("input", handleInteraction, {
                                            once: true,
                                        })
                                    })
                                }

                                resolve()
                            },
                            onFormSubmit: function (form) {
                                if (onSubmit) onSubmit()
                            },
                        })

                        // ... [rest of the backup timeout code]
                    } catch (error) {
                        reject(error)
                    }
                } else if (attempts < 50) {
                    setTimeout(checkHbspt, 100)
                } else {
                    reject(new Error("HubSpot API not available after 50 attempts"))
                }
            }

            checkHbspt()
        })
    }, [portalId, formId, region, target, onFormInteraction])

    const loadScript = React.useCallback(() => {
        const formVersion = getFormVersion()
        const scriptSrc =
            formVersion === "v2"
                ? "https://js.hsforms.net/forms/embed/v2.js"
                : `https://js.hsforms.net/forms/embed/${portalId}.js`

        return new Promise((resolve, reject) => {
            // Check if script already exists
            const existingScript = document.querySelector(
                `script[src="${scriptSrc}"]`
            )
            if (existingScript) {
                scriptLoaded.current = true

                if (formVersion === "v2" && !formCreated.current) {
                    createV2Form()
                        .then(resolve)
                        .catch(reject)
                } else {
                    resolve()
                }
                return
            }

            // Clean up conflicting globals
            if (window.hbspt) delete window.hbspt
            if (window.HubspotFormsV4) {
                try {
                    delete window.HubspotFormsV4
                } catch (e) { }
            }

            const script = document.createElement("script")
            script.src = scriptSrc
            script.async = true
            script.defer = true

            script.onload = () => {
                scriptLoaded.current = true

                if (formVersion === "v2") {
                    // For v2, create the form programmatically
                    setTimeout(() => {
                        createV2Form()
                            .then(resolve)
                            .catch(reject)
                    }, 100)
                } else {
                    // For embed, just wait for auto-initialization
                    setTimeout(resolve, 500)
                }
            }

            script.onerror = reject
            document.head.appendChild(script)
        })
    }, [getFormVersion, portalId, createV2Form])

    React.useEffect(() => {
        if (!isClient || !formId || !portalId) return

        loadScript()
            .then(() => {
                setIsLoading(false)
            })
            .catch(error => {
                setHasError(true)
                setIsLoading(false)
            })
    }, [isClient, formId, portalId, loadScript])

    if (!isClient) {
        return <div>Loading...</div>
    }

    const formVersion = getFormVersion()

    // Always render the container, even when loading
    return (
        <div>
            {formVersion === "embed" ? (
                <div
                    className="hs-form-frame"
                    data-region={region}
                    data-form-id={formId}
                    data-portal-id={portalId}
                />
            ) : (
                <div id={`hubspot-form-${formId}`}></div>
            )}

            {hasError && (
                <div style={{ color: "red", marginTop: "10px" }}>
                    Failed to load form
                </div>
            )}

            {isLoading && (
                <div style={{ marginTop: "10px" }}>{/* Loading form... */}</div>
            )}
        </div>
    )
}
